/**
 * useRoutingSettings Hook
 * Manage the tenant's bot→human escalation config — the phrases that hand a WhatsApp
 * conversation to a team member, plus the master switch over automatic escalation.
 * Mirrors useAutomationSettings.ts's GET/PATCH shape.
 *
 * Saves are partial (PATCH), so each control writes only its own field rather than
 * round-tripping the whole object.
 */

import adminApi, {
  type EscalationSettings,
  type RoutingPhraseTestResult,
} from '@/services/admin.api';
import { useCallback, useEffect, useState } from 'react';

const EMPTY_ESCALATION: EscalationSettings = {
  enabled: true,
  keywords: [],
  mode: 'extend',
  exclusions: [],
  lowConfidenceThreshold: 50,
  handoffMessage: '',
  aiFailureMessage: '',
  assignmentTarget: { kind: 'anyone' },
};

export const useRoutingSettings = () => {
  const [escalation, setEscalation] = useState<EscalationSettings>(EMPTY_ESCALATION);
  const [builtinExamples, setBuiltinExamples] = useState<string[]>([]);
  const [defaults, setDefaults] = useState({ handoffMessage: '', aiFailureMessage: '', lowConfidenceThreshold: 50 });
  const [limits, setLimits] = useState({
    maxKeywords: 200,
    maxKeywordLength: 80,
    maxHandoffMessageLength: 1000,
    maxAiFailureMessageLength: 1000,
    maxAssignmentRoleIds: 50,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getRoutingSettings();
      setEscalation(data.escalation);
      setBuiltinExamples(data.builtinExamples || []);
      if (data.defaults) setDefaults(data.defaults);
      if (data.limits) setLimits(data.limits);
    } catch (err: any) {
      setError(err.message || 'Failed to load escalation settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Persist one or more fields. Returns true on success so a caller can keep local
   * edit state (an in-progress list edit) rather than discarding it on failure.
   * The server echoes the normalised result — trimmed, deduped — and that echo is what
   * we store, so the UI always shows exactly what the webhook will actually match.
   */
  const save = useCallback(
    async (patch: Partial<EscalationSettings>): Promise<boolean> => {
      try {
        setSaving(true);
        setError('');
        setSuccess('');
        const data = await adminApi.updateRoutingSettings(patch);
        setEscalation(data.escalation);
        setSuccess('Escalation settings saved');
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to save escalation settings');
        return false;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const dismissSuccess = useCallback(() => setSuccess(''), []);

  /**
   * Dry-run a message against the SAVED config. Deliberately not evaluated against
   * local edit state: the answer must reflect what the bot will actually do right now,
   * and the only thing that knows that is the server running the same evaluator the
   * webhook uses.
   */
  const testPhrase = useCallback(async (message: string): Promise<RoutingPhraseTestResult | null> => {
    try {
      setTesting(true);
      setError('');
      return await adminApi.testRoutingPhrase(message);
    } catch (err: any) {
      setError(err.message || 'Failed to test the message');
      return null;
    } finally {
      setTesting(false);
    }
  }, []);

  return {
    escalation,
    builtinExamples,
    defaults,
    limits,
    loading,
    saving,
    testing,
    error,
    success,
    save,
    testPhrase,
    dismissSuccess,
    refetch: fetchSettings,
  };
};
